import React from 'react'
import { css, cx } from '@emotion/css'
import { monotonic } from '../../util'
import { TextBox } from '../form-text-box'
import { paragraphTextCss } from '../text'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { ViewerLoader } from '../../components/viewer'

interface WrappedTimePickerProps {
  readonly hour?: number
  readonly minute?: number
  readonly onHourChange?: (hour: number) => void
  readonly onMinuteChange?: (minute: number) => void
  readonly 'data-testid'?: string
}

interface TimePickerProps extends WrappedTimePickerProps {
  readonly showPeriod: boolean
}

interface State {
  readonly clickEvent: number
  readonly mouseDownEvent: number
  readonly focusEvent: number
  readonly deactivationEvent: number
  readonly nextSelectionEvent: number
  readonly previousSelectionEvent: number
  readonly selectDigitEvent: number
  readonly selectedDigit?: number
  readonly selection: Selection
}

type Selection = 'none' | 'hour' | 'minute' | 'period'

export class TimePicker extends React.Component<TimePickerProps, State> {
  public readonly state: State = {
    clickEvent: 0,
    mouseDownEvent: 0,
    focusEvent: 0,
    deactivationEvent: 0,
    nextSelectionEvent: 0,
    previousSelectionEvent: 0,
    selectDigitEvent: 0,
    selection: 'none'
  }

  private nextInterval = monotonic()
  private inputRef: HTMLInputElement
  private cleanup

  public componentDidMount() {
    const onMouseDown = (e) => {
      if (e.target !== this.inputRef) {
        this.setState({ deactivationEvent: this.nextInterval() })
      }
    }

    window.addEventListener('mousedown', onMouseDown)

    this.cleanup = () => {
      window.removeEventListener('mousedown', onMouseDown)
    }
  }

  public componentWillUnmount() {
    this.cleanup()
  }

  public componentDidUpdate(_previousProps, previousState) {
    const {
      nextSelectionEvent,
      previousSelectionEvent,
      clickEvent,
      mouseDownEvent,
      focusEvent,
      deactivationEvent,
      selection,
      selectedDigit,
      selectDigitEvent
    } = this.state

    if (
      this.inputRef &&
      (previousState.nextSelectionEvent !== nextSelectionEvent ||
        previousState.previousSelectionEvent !== previousSelectionEvent)
    ) {
      this.updateSelection(nextSelectionEvent > previousSelectionEvent)
    }

    if (previousState.selection !== selection) {
      this.setState({ selectedDigit: undefined })
    }

    if (
      selectedDigit !== undefined &&
      previousState.selectDigitEvent !== selectDigitEvent
    ) {
      this.handleDigitSelection(selectedDigit, previousState.selectedDigit)
    }

    if (
      previousState.clickEvent !== clickEvent ||
      previousState.focusEvent !== focusEvent ||
      previousState.deactivationEvent !== deactivationEvent
    ) {
      if (this.shouldDeactivate(clickEvent, focusEvent, deactivationEvent)) {
        this.setState({ selection: 'none' })
        return
      }

      if (
        focusEvent > mouseDownEvent &&
        focusEvent > clickEvent &&
        clickEvent >= mouseDownEvent
      ) {
        this.setState({ selection: 'hour' })
        return
      }

      const caretPosition = this.inputRef.selectionStart
      const selection = this.getSelectionBasedOnCaret(caretPosition)
      this.setState({ selection })
    }

    this.setInputSelectionRange()
  }

  private shouldDeactivate = (
    clickEvent: number,
    focusEvent: number,
    deactivationEvent: number
  ) => {
    return deactivationEvent > clickEvent && deactivationEvent > focusEvent
  }

  public getSelectionBasedOnCaret(caretPosition: number | null) {
    if (caretPosition === null) {
      return 'hour'
    }

    if (this.props.showPeriod && caretPosition >= 6) {
      return 'period'
    } else if (caretPosition >= 3) {
      return 'minute'
    } else {
      return 'hour'
    }
  }

  public render() {
    const { hour, minute, showPeriod } = this.props

    let transformedHour = hour

    if (hour !== undefined && showPeriod) {
      transformedHour = hour % 12
      if (transformedHour === 0) {
        transformedHour = 12
      }
    }

    const formatWithLeadingZero = (value?: number) => {
      if (value === undefined) {
        return '--'
      }

      if (value < 10) {
        return `0${value}`
      }

      return value.toString()
    }

    const getPeriodDisplay = (showPeriod: boolean, hour?: number) => {
      if (!showPeriod) {
        return ''
      }
      if (hour === undefined) {
        return ' --'
      }
      return hour > 11 ? ' PM' : ' AM'
    }

    const hourDisplay = formatWithLeadingZero(transformedHour)
    const minuteDisplay = formatWithLeadingZero(minute)
    const periodDisplay = getPeriodDisplay(showPeriod, hour)

    return (
      <TextBox
        className={cx(
          paragraphTextCss,
          css`
            cursor: default;
            color: ${theme.color.neutral100};
            width: auto;
            padding-right: 5px;
          `
        )}
        size={showPeriod ? 8 : 5}
        value={`${hourDisplay}:${minuteDisplay}${periodDisplay}`}
        onMouseDown={this.handleMouseDown}
        onClick={this.handleClick}
        onKeyDown={this.handleKeyDown}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        inputRef={(ref) => ref && (this.inputRef = ref)}
        onChange={this.handleChange}
        data-testid={this.props['data-testid']}
      />
    )
  }

  private handleChange = (e) => {
    // Updates to value handled through state changes
    e.preventDefault()
    e.stopPropagation()
  }

  private handleBlur = () => {
    this.setState({
      deactivationEvent: this.nextInterval()
    })
  }

  private handleFocus = () => {
    this.setState({
      focusEvent: this.nextInterval()
    })
  }

  private handleMouseDown = () => {
    this.setState({ mouseDownEvent: this.nextInterval() })
  }

  private handleClick = () => {
    this.setState({ clickEvent: this.nextInterval() })
  }

  private handleKeyDown = (e) => {
    const DIGIT_KEYS = [...Array(10).keys()].map((i) => 48 + i) // Key codes for digits 0-9
    const KEY_TAB = 9
    const KEY_DOWN_ARROW = 40
    const KEY_UP_ARROW = 38
    const KEY_LEFT_ARROW = 37
    const KEY_RIGHT_ARROW = 39
    const KEY_A = 65
    const KEY_P = 80

    if (DIGIT_KEYS.includes(e.keyCode)) {
      this.handleDigitKey(e.keyCode - 48)
      e.preventDefault()
      return
    }

    switch (e.keyCode) {
      case KEY_TAB:
        this.handleTabKey()
        e.preventDefault()
        break

      case KEY_DOWN_ARROW:
        this.handleArrowDown()
        e.preventDefault()
        break

      case KEY_UP_ARROW:
        this.handleArrowUp()
        e.preventDefault()
        break

      case KEY_LEFT_ARROW:
        this.handlePreviousSelection()
        e.preventDefault()
        break

      case KEY_RIGHT_ARROW:
        this.handleNextSelection()
        e.preventDefault()
        break

      case KEY_A:
        this.handlePeriodChange('AM')
        e.preventDefault()
        break

      case KEY_P:
        this.handlePeriodChange('PM')
        e.preventDefault()
        break

      default:
        e.preventDefault()
        break
    }
  }

  private handleDigitKey = (digit: number) => {
    this.setState({
      selectedDigit: digit,
      selectDigitEvent: this.nextInterval()
    })
  }

  private handleTabKey = () => {
    const { selection } = this.state
    const { showPeriod } = this.props

    if (selection === 'period' || (selection === 'minute' && !showPeriod)) {
      this.setState({ deactivationEvent: this.nextInterval() })
    } else {
      this.setState({ nextSelectionEvent: this.nextInterval() })
    }
  }

  private handleArrowDown = () => {
    const { selection } = this.state
    const { onHourChange, onMinuteChange, hour, minute, showPeriod } =
      this.props

    if (selection === 'hour' && onHourChange) {
      this.changeHour(hour, showPeriod, -1)
    } else if (selection === 'minute' && onMinuteChange) {
      this.changeMinute(minute, -1)
    } else if (selection === 'period' && onHourChange) {
      this.togglePeriod(hour)
    }

    this.setState({ selectedDigit: undefined })
  }

  private handleArrowUp = () => {
    const { selection } = this.state
    const { onHourChange, onMinuteChange, hour, minute, showPeriod } =
      this.props

    if (selection === 'hour' && onHourChange) {
      this.changeHour(hour, showPeriod, 1)
    } else if (selection === 'minute' && onMinuteChange) {
      this.changeMinute(minute, 1)
    } else if (selection === 'period' && onHourChange) {
      this.togglePeriod(hour)
    }

    this.setState({ selectedDigit: undefined })
  }

  private changeHour = (
    hour: number | undefined,
    showPeriod: boolean,
    increment: number
  ) => {
    if (hour === undefined) {
      this.props.onHourChange?.(this.getInitialHour(showPeriod, increment))
      return
    }

    let newHour = hour + increment

    newHour = showPeriod
      ? this.adjustHourForPeriod(hour, increment, newHour)
      : this.adjustHourFor24HourFormat(newHour)

    this.props.onHourChange?.(newHour)
  }

  private getInitialHour = (showPeriod: boolean, increment: number): number => {
    let newHour: number

    if (showPeriod) {
      newHour = increment > 0 ? 1 : 11
    } else {
      newHour = increment > 0 ? 0 : 23
    }

    return newHour
  }

  private adjustHourForPeriod = (
    hour: number,
    increment: number,
    newHour: number
  ): number => {
    if (hour === 0 && increment < 0) {
      return 11
    }
    if (hour === 12 && increment > 0) {
      return 23
    }
    return newHour
  }

  private adjustHourFor24HourFormat = (newHour: number): number => {
    if (newHour < 0) {
      return 23
    }
    if (newHour > 23) {
      return 0
    }
    return newHour
  }

  private changeMinute = (minute: number | undefined, increment: number) => {
    if (minute === undefined || (minute === 0 && increment < 0)) {
      this.props.onMinuteChange?.(59)
    } else {
      this.props.onMinuteChange?.(minute + increment)
    }
  }

  private togglePeriod = (hour: number | undefined) => {
    if (hour === undefined) {
      this.props.onHourChange?.(0)
    } else if (hour < 12) {
      this.props.onHourChange?.(hour + 12)
    } else {
      this.props.onHourChange?.(hour - 12)
    }
  }

  private handlePreviousSelection = () => {
    this.setState({ previousSelectionEvent: this.nextInterval() })
  }

  private handleNextSelection = () => {
    this.setState({ nextSelectionEvent: this.nextInterval() })
  }

  private handlePeriodChange = (period: 'AM' | 'PM') => {
    const { onHourChange, hour } = this.props
    if (!onHourChange || hour === undefined) {
      return
    }

    if (period === 'AM' && hour >= 12) {
      onHourChange(hour - 12)
    } else if (period === 'PM' && hour < 12) {
      onHourChange(hour + 12)
    }
  }

  private setInputSelectionRange = () => {
    const { selection } = this.state

    if (selection === 'none') {
      this.setRangeIfNotSet(0, 0)
    } else if (selection === 'hour') {
      this.setRangeIfNotSet(0, 2)
    } else if (selection === 'minute') {
      this.setRangeIfNotSet(3, 5)
    } else if (selection === 'period') {
      this.setRangeIfNotSet(6, 8)
    }
  }

  private setRangeIfNotSet = (start, end) => {
    if (this.inputRef) {
      if (
        this.inputRef.selectionStart !== start ||
        this.inputRef.selectionEnd !== end
      ) {
        this.inputRef.setSelectionRange(start, end)
      }
    }
  }

  private updateSelection = (advance: boolean) => {
    this.setState(({ selection }) => {
      if (selection === 'none') {
        return { selection: 'hour' }
      } else if (selection === 'hour') {
        if (advance) {
          return { selection: 'minute' }
        }
      } else if (selection === 'minute') {
        if (advance) {
          if (this.props.showPeriod) {
            return { selection: 'period' }
          }
        } else {
          return { selection: 'hour' }
        }
      } else if (selection === 'period') {
        if (!advance) {
          return { selection: 'minute' }
        }
      }
      return { selection }
    })
  }

  private handleDigitSelection = (
    selectedDigit: number,
    previousSelectedDigit?: number
  ) => {
    const { selection } = this.state
    const { onHourChange, onMinuteChange, hour, showPeriod } = this.props

    if (selection === 'hour' && onHourChange) {
      if (previousSelectedDigit === undefined) {
        if (showPeriod) {
          if (hour !== undefined && hour > 11) {
            onHourChange(12 + selectedDigit)
          } else {
            onHourChange(selectedDigit)
          }
          if (selectedDigit > 1) {
            this.setState({ nextSelectionEvent: this.nextInterval() })
          }
        } else {
          onHourChange(selectedDigit)
          if (selectedDigit > 2) {
            this.setState({ nextSelectionEvent: this.nextInterval() })
          }
        }
      } else {
        if (showPeriod) {
          let newHourOffset = selectedDigit
          if (previousSelectedDigit === 1 && selectedDigit <= 2) {
            newHourOffset = selectedDigit + 10
          }

          if (hour !== undefined && hour > 11) {
            if (newHourOffset === 12) {
              onHourChange(12)
            } else {
              onHourChange(12 + newHourOffset)
            }
          } else {
            if (newHourOffset === 12) {
              onHourChange(0)
            } else {
              onHourChange(newHourOffset)
            }
          }
        } else {
          let newHourOffset = selectedDigit
          if (previousSelectedDigit === 1) {
            newHourOffset = selectedDigit + 10
          } else if (previousSelectedDigit === 2 && selectedDigit <= 3) {
            newHourOffset = selectedDigit + 20
          }

          onHourChange(newHourOffset)
        }
        this.setState({ nextSelectionEvent: this.nextInterval() })
      }
    } else if (selection === 'minute' && onMinuteChange) {
      if (previousSelectedDigit === undefined) {
        onMinuteChange(selectedDigit)

        if (selectedDigit > 5) {
          this.setState({ nextSelectionEvent: this.nextInterval() })
        }
      } else {
        onMinuteChange(parseInt(`${previousSelectedDigit}${selectedDigit}`, 10))
        this.setState({ nextSelectionEvent: this.nextInterval() })
      }
    }
  }
}

export function TimePickerWrapped(props: WrappedTimePickerProps) {
  return (
    <ViewerLoader fetchPolicy="cache-first">
      {({ viewer }) => {
        return (
          <TimePicker
            {...props}
            showPeriod={
              viewer && viewer.datePreference
                ? viewer.datePreference.showPeriod
                : true
            }
          />
        )
      }}
    </ViewerLoader>
  )
}
